import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditSummary } from "../../Models/Credit";
import { RefCodeType } from "../../Models/ReferenceCodes";
import { ROUTE_PATHS } from "../../Routes/constants";
import { getCreditsSummary } from "../../Services/CreditService";
import {
  BulkSnoozeDatesResponse,
  getBulkSnoozeDates,
} from "../../Services/SnoozeService";
import { sendEvent } from "../../Utils/analytics";
import { getRemoteValue } from "../../Utils/firebase";
import useAuthState from "../../Utils/store";
import BulkPayModal, { BulkPayAllocation } from "../BulkPayment/BulkPayModal";
import { useMediaQuery } from "../CustomHook/useMediaQuery";
import BulkSnoozeFlow from "./BulkSnoozeFlow";
import DesktopTotalDebts from "./DesktopTotalDebts";
import MobileTotalDebts from "./MobileTotalDebts";

const TotalDebts = () => {
  const payments = useAuthState((state) => state.payments);
  const setPaymentMethod = useAuthState((state) => state.setPaymentMethod);
  const setIsAllPayment = useAuthState((state) => state.setIsAllPayment);
  const navigate = useNavigate();
  const [totalDebts, setTotalDebts] = useState<CreditSummary>();
  /** CRL-3241: "Toplu Öde" now opens the bulk pay modal before checkout. */
  const [bulkPayOpen, setBulkPayOpen] = useState(false);

  const [snoozeAvailability, setSnoozeAvailability] = useState<BulkSnoozeDatesResponse | undefined>(undefined);

  const isDesktop = useMediaQuery();

  /** CRL-3241: increments open the bulk snooze flow (see BulkSnoozeFlow). */
  const [bulkSnoozeStartSignal, setBulkSnoozeStartSignal] = useState(0);

  const getSnoozeAvailability = useCallback(async () => {
    const response = await getBulkSnoozeDates();
    setSnoozeAvailability(response);
  }, []);

  const handleAllDebts = useCallback(async (options?: { trackView?: boolean }) => {
    const res = await getCreditsSummary();
    if (options?.trackView !== false) {
      sendEvent(`view_dashboard_page`, {
        total_debt_amount: res?.totalAmount,
        total_late_status_debt_amount: res?.totalLateAmount,
        active_credit_count: res?.activeCreditCount,
        late_credit_count: res?.lateCreditCount,
      });
    }
    setTotalDebts(res);
  }, []);

  useEffect(() => {
    void handleAllDebts();
    void getSnoozeAvailability();
  }, [handleAllDebts, getSnoozeAvailability]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void handleAllDebts({ trackView: false });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [handleAllDebts]);

  const logBulkSnoozeCalendarPage = async (
    paymentPlansCount: number,
    snoozablePaymentPlansCount: number,
    notSnoozablePaymentPlansCount: number,
  ) => {
    sendEvent('view_bulk_snooze_calendar_page', {
      total_credit_count: paymentPlansCount,
      snoozable_credit_count: snoozablePaymentPlansCount,
      not_snoozable_credit_count: notSnoozablePaymentPlansCount,
    });
  };

  const onBulkSnoozeClick = () => {
    setBulkSnoozeStartSignal((s) => s + 1);
    logBulkSnoozeCalendarPage(
      payments?.length ?? 0,
      payments?.filter((p) => p.snoozeAvailable).length ?? 0,
      payments?.filter((p) => !p.snoozeAvailable).length ?? 0,
    );
  };

  const onBulkPayClick = () => {
    sendEvent(`view_bulk_pay_modal`, {
      total_debt_amount: totalDebts?.totalAmount,
      payment_count: payments?.length ?? 0,
    });
    setBulkPayOpen(true);
  };

  /** `customAmount` set → PaymentType.CUSTOM on checkout; undefined → pay all. */
  const onBulkPayConfirm = (
    customAmount?: number,
    allocation?: BulkPayAllocation
  ) => {
    const isCustom = customAmount !== undefined && customAmount > 0;
    // keep the allocation only when it reached at least one payment; the
    // checkout summary falls back to the full list otherwise
    const hasAllocation =
      allocation !== undefined &&
      (allocation.coveredCreditIds.length > 0 || allocation.partial !== undefined);
    sendEvent(`click_bulk_pay_confirm_button`, {
      payment_type: isCustom ? "CUSTOM" : "ALL",
      amount: isCustom ? customAmount : totalDebts?.totalAmount,
    });
    setBulkPayOpen(false);
    setPaymentMethod(
      isCustom
        ? {
            type: RefCodeType.ALL,
            amount: customAmount,
            customAllocation: hasAllocation ? allocation : undefined,
          }
        : { type: RefCodeType.ALL }
    );
    setIsAllPayment(true);
    navigate(ROUTE_PATHS.CHECKOUT);
  };

  return (
    <>
      <section id="totalDebts" className="px-4 py-8">
        <h2 className="text-[22px] md:text-[28px] font-bold text-cardValue">
          {getRemoteValue("debts")}
        </h2>
        <p className="pt-2 xs:text-sm md:text-base font-normal text-cardLabel">
          {getRemoteValue("debts_desciription")}
        </p>

        {isDesktop ? (
          <DesktopTotalDebts
            totalDebtsData={totalDebts}
            snoozeAvailability={snoozeAvailability}
            onBulkSnoozeClick={onBulkSnoozeClick}
            onBulkPayClick={onBulkPayClick} />
        ) : (
          <MobileTotalDebts
            totalDebtsData={totalDebts}
            snoozeAvailability={snoozeAvailability}
            onBulkSnoozeClick={onBulkSnoozeClick}
            onBulkPayClick={onBulkPayClick} />
        )}
      </section>
      <BulkSnoozeFlow
        startSignal={bulkSnoozeStartSignal}
        payments={payments ?? []}
        snoozeAvailability={snoozeAvailability}
      />
      <BulkPayModal
        open={bulkPayOpen}
        onClose={() => setBulkPayOpen(false)}
        payments={payments ?? []}
        totalAmount={totalDebts?.totalAmount ?? 0}
        onConfirm={onBulkPayConfirm}
      />
    </>
  );
};

export default TotalDebts;
